<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="uploadInProgress"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card
			class="sgnl-pb-4"
			data-test="sig-cloud-storage-create-directory-dialog"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				Create Directory
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<v-form
					ref="mainForm"
					v-model="formValid"
				>
					<div class="input-group">
						<div class="field-label">Destination</div>
						<v-text-field
							placeholder="Directory Name"
							v-model="directoryName"
							:rules="[rules.required, rules.noSpace, rules.maxSize, rules.noRestrictedCharacters]"
							autofocus
							@keydown.enter.prevent="createDirectory"
							data-test="directory-name-input"
						/>
					</div>
				</v-form>
				<div
					class="sgnl-paragraph-s-regular"
					v-if="formValid"
				>
					The new directory will be created at <br />
					<span class="sgnl-code-default">./{{ absolutePath }}</span>
				</div>

				<v-expand-transition>
					<v-alert
						v-show="showError"
						:icon="false"
						border="start"
						class="sgnl-paragraph-s-regular sgnl-p-8 sgnl-pl-12"
						:type="alertType"
						variant="outlined"
						elevation="0"
					>
						{{ errorMessage }}
					</v-alert>
				</v-expand-transition>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
					>Cancel</v-btn
				>
				<v-btn
					variant="flat"
					color="primary"
					@click="createDirectory"
					:disabled="!formValid"
					:loading="uploadInProgress"
					data-test="create-directory-button"
				>
					Create Directory
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import { sdk } from "@/js/sdk";
import { SdkError } from "@signaloid/cloud-compute-engine-client";
import axios from "axios";
import * as dsUtil from "@/components/DataSources/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

export default defineComponent({
	name: "SigCloudStorageCreateDirectoryDialog",
	components: { Close16 },
	props: {
		showDialog: {
			type: Boolean,
			default: false,
		},
		parentDir: {
			type: String,
			default: "",
		},
	},
	emits: {
		"update:showDialog": (_data) => true,
		"directory-created": () => true,
	},
	data: () => ({
		directoryName: "",
		formValid: false,
		uploadInProgress: false,
		alertType: "info",
		showError: false,
		errorMessage: "",
		uploadTaskStatus: "",
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		absolutePath() {
			return this.parentDir ? `${this.parentDir}/${this.directoryName.trim()}` : `${this.directoryName.trim()}`;
		},
		rules() {
			return {
				required: (value) => !!value || "Required.",
				counter: (value) => value.length <= 50 || "Directory path length should be less than 50 characters",
				maxSize: (value) =>
					value.length < dsUtil.awsS3MaxPathSizeBytes ||
					`Directory path length should be less than ${dsUtil.awsS3MaxPathSizeBytes} Bytes`,
				noSpace: (value) => value.indexOf(" ") == -1 || "Directory path cannot have spaces",
				noRestrictedCharacters: (value) => {
					return (
						dsUtil.awsS3RestrictedCharacters.every((character) => {
							return value.indexOf(character) == -1;
						}) ||
						`Directory path cannot contain ${dsUtil.awsS3RestrictedCharacters.reduce((t, c, idx) => {
							return idx == dsUtil.awsS3RestrictedCharacters.length - 1
								? t + " , or " + c
								: t + " , " + c;
						})}`
					);
				},
			};
		},
	},
	methods: {
		containsS3RestrictedCharacters(value) {
			return this.awsS3RestrictedCharacters.every((character) => {
				return value.indexOf(character) == -1;
			});
		},
		handleDismiss() {
			if (this.uploadInProgress) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("update:showDialog", false);
		},
		async createDirectory() {
			if (this.formValid) {
				this.uploadInProgress = true;
				try {
					await sdk.files.createDirectory(`${dsUtil.sanitizeStoragePath(this.absolutePath)}/`);
					this.$emit("directory-created");
					this.closeDialog();
				} catch (error) {
					monitoringCaptureError(error, "Create directory in user cloud storage");
					this.uploadInProgress = false;

					if (error instanceof SdkError) {
						this.errorMessage = error.message;
					} else if (axios.isAxiosError(error)) {
						this.errorMessage =
							"Failed to create the directory. Make sure you have an active internet connection.";
					} else if (error instanceof Error) {
						this.errorMessage = error.message;
					} else {
						this.errorMessage = "An unexpected error occurred.";
					}
					this.showError = true;
					this.alertType = "warning";
				}
			}
		},
	},
	mounted() {
		this.newValue = this.value;
	},
});
</script>
